/** @jsxImportSource @emotion/react */
import React, { PureComponent } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core'
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import getLogger from '@signal/core/fe-logging'
import { makeLightTheme } from '@signal/theme/theme'
import UnexpectedError from '@signal/components/UnexpectedError'

type PublicErrorBoundaryProps = {
  children?: React.ReactNode
}

type PublicErrorBoundaryState = {
  hasError: boolean
}

// Unlike ThemeSafeErrorBoundary, never redirects to login or the account
// selector. Carries its own theme so it renders even if the surrounding one is gone.
export class PublicErrorBoundary extends PureComponent<
  PublicErrorBoundaryProps,
  PublicErrorBoundaryState
> {
  private readonly lightTheme = makeLightTheme()

  state: PublicErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): PublicErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, { componentStack }: React.ErrorInfo): void {
    getLogger().exception(error, componentStack)
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <MuiThemeProvider theme={this.lightTheme}>
          <EmotionThemeProvider theme={this.lightTheme}>
            <UnexpectedError willRedirect={false} />
          </EmotionThemeProvider>
        </MuiThemeProvider>
      )
    }

    return this.props.children
  }
}

export default PublicErrorBoundary
